import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import NewsletterStatusBadge from "./NewsletterStatusBadge";
import NewsletterSectionEditor from "./NewsletterSectionEditor";
import NewsletterPreview from "./NewsletterPreview";
import SendTestModal from "./SendTestModal";
import SendConfirmModal from "./SendConfirmModal";
import styles from "./Newsletters.module.css";
import { formatNewsletterDate } from "./newsletterUtils";
import { uploadNewsletterImage, validateNewsletterImage } from "./newsletterUpload";

const initialDraft = {
  subject: "",
  preheader: "",
  hero_title: "",
  hero_copy: "",
  cta_text: "",
  cta_url: "",
  sections: [],
  template_data: {},
  sender_name: "",
  sender_email: "",
  reply_to_email: "",
};

const initialSenderOptions = {
  sender_name: "",
  sender_emails: [],
  default_sender_email: "",
  default_reply_to_email: "",
};
const initialCampaignLists = [];

function isValidEmail(value) {
  if (!value) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isValidUrl(value) {
  if (!value) return true;
  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return true;
  } catch (error) {
    return false;
  }
}

function getCampaignListAudienceCount(item) {
  const candidates = [
    item?.audience_count,
    item?.totalcontacts,
    item?.total_contacts,
    item?.contactcount,
    item?.contact_count,
    item?.subscribercount,
    item?.subscriber_count,
    item?.count,
  ];
  const found = candidates.find(
    (value) => value !== undefined && value !== null && value !== "",
  );
  if (found === undefined) return null;
  if (typeof found === "number") {
    return Number.isFinite(found) ? Math.max(0, Math.trunc(found)) : null;
  }
  if (typeof found === "string") {
    const match = found.match(/\d+/);
    return match ? Number(match[0]) : null;
  }
  return null;
}

function getSelectedCampaignAudienceCount(campaignLists, selectedListKey) {
  if (!selectedListKey) return null;
  const selected = (campaignLists || []).find(
    (item) => String(item?.list_key || "") === String(selectedListKey),
  );
  if (!selected) return null;
  return getCampaignListAudienceCount(selected);
}

function NewsletterEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [draft, setDraft] = useState(initialDraft);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewViewport, setPreviewViewport] = useState("desktop");
  const [previewError, setPreviewError] = useState("");
  const [testOpen, setTestOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [senderOptions, setSenderOptions] = useState(initialSenderOptions);
  const [senderOptionsLoading, setSenderOptionsLoading] = useState(true);
  const [senderOptionsError, setSenderOptionsError] = useState("");
  const [campaignLists, setCampaignLists] = useState(initialCampaignLists);
  const [campaignListsLoading, setCampaignListsLoading] = useState(false);
  const [campaignListsError, setCampaignListsError] = useState("");
  const [uploadingSectionIds, setUploadingSectionIds] = useState([]);
  const [generatingSectionIds, setGeneratingSectionIds] = useState([]);
  const [regeneratingPromptIds, setRegeneratingPromptIds] = useState([]);
  const [sectionAiDialog, setSectionAiDialog] = useState({
    open: false,
    index: null,
    note: "",
  });
  const [heroImageUploading, setHeroImageUploading] = useState(false);
  const [spotlightImageUploading, setSpotlightImageUploading] = useState(false);
  const isDraft = draft.status === "draft";
  const isApproved = draft.status === "approved";
  const isSent = draft.status === "sent";
  const hasZohoCampaign = Boolean(String(draft.campaign_id || "").trim());
  const selectedListAudienceCount = useMemo(
    () => getSelectedCampaignAudienceCount(campaignLists, draft.campaign_list_key),
    [campaignLists, draft.campaign_list_key],
  );
  const effectiveAudienceCount =
    selectedListAudienceCount ?? Number(draft.audience_count || 0);

  const applySenderDefaults = (draftData, optionData) => ({
    ...draftData,
    sender_name: draftData?.sender_name || optionData?.sender_name || "",
    sender_email:
      draftData?.sender_email || optionData?.default_sender_email || "",
    reply_to_email:
      draftData?.reply_to_email || optionData?.default_reply_to_email || "",
  });

  const fetchSenderOptions = async () => {
    setSenderOptionsLoading(true);
    setSenderOptionsError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/sender-options`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to load sender settings");
      }
      const nextOptions = {
        ...initialSenderOptions,
        ...data.data,
        sender_emails: Array.isArray(data?.data?.sender_emails)
          ? data.data.sender_emails
          : [],
      };
      setSenderOptions(nextOptions);
      setDraft((prev) => applySenderDefaults(prev, nextOptions));
    } catch (error) {
      setSenderOptionsError(error.message || "Unable to load sender settings");
    } finally {
      setSenderOptionsLoading(false);
    }
  };

  const fetchDraft = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/${id}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to load draft");
      }
      setDraft(applySenderDefaults({
        ...initialDraft,
        ...data.data,
        sections: Array.isArray(data?.data?.sections)
          ? data.data.sections.map((section) => ({
              ...section,
              theme: section?.theme || "light",
            }))
          : [],
        template_data: data?.data?.template_data || {},
      }, senderOptions));
    } catch (error) {
      toast.error(error.message || "Unable to load draft");
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignLists = async () => {
    setCampaignListsLoading(true);
    setCampaignListsError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/campaign-lists`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to load campaign lists");
      }
      setCampaignLists(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      setCampaignLists([]);
      setCampaignListsError(error.message || "Unable to load campaign lists");
    } finally {
      setCampaignListsLoading(false);
    }
  };

  useEffect(() => {
    fetchSenderOptions();
    fetchCampaignLists();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchDraft();
    // eslint-disable-next-line
  }, [id, senderOptions.default_sender_email, senderOptions.default_reply_to_email, senderOptions.sender_name]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!draft.subject && !draft.hero_title && !draft.hero_copy) return;
      try {
        setPreviewError("");
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/render-preview`,
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              subject: draft.subject,
              preheader: draft.preheader,
              hero_title: draft.hero_title,
              hero_copy: draft.hero_copy,
              cta_text: draft.cta_text,
              cta_url: draft.cta_url,
              sections: draft.sections,
              template_data: {
                first_name: "Amit",
                company_name: "Acme",
                workspace_name: "Acme Workspace",
                ...draft.template_data,
              },
            }),
          },
        );
        const data = await response.json();
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || "Preview service unavailable");
        }
        setPreviewHtml(data.html || "");
      } catch (error) {
        setPreviewHtml("");
        setPreviewError(error.message || "Preview API unavailable, showing local preview.");
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [draft]);

  const updateDraftField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const updateTemplateField = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      template_data: {
        ...(prev.template_data || {}),
        [key]: value,
      },
    }));
  };

  const validateSections = () => {
    if (
      draft?.template_data?.hero_image_url &&
      !isValidUrl(draft.template_data.hero_image_url.trim())
    ) {
      return "Hero image URL is invalid";
    }

    const invalidSection = (draft.sections || []).find((section) => {
      const hasCtaText = Boolean(section?.cta_text?.trim());
      const hasCtaUrl = Boolean(section?.cta_url?.trim());
      const hasImageUrl = Boolean(section?.image_url?.trim());

      if ((hasCtaText && !hasCtaUrl) || (!hasCtaText && hasCtaUrl)) {
        return true;
      }
      if (hasCtaUrl && !isValidUrl(section.cta_url.trim())) {
        return true;
      }
      if (hasImageUrl && !isValidUrl(section.image_url.trim())) {
        return true;
      }
      return false;
    });

    if (!invalidSection) return null;

    const hasCtaText = Boolean(invalidSection?.cta_text?.trim());
    const hasCtaUrl = Boolean(invalidSection?.cta_url?.trim());
    const hasImageUrl = Boolean(invalidSection?.image_url?.trim());

    if ((hasCtaText && !hasCtaUrl) || (!hasCtaText && hasCtaUrl)) {
      return "Each section CTA needs both text and URL";
    }
    if (hasCtaUrl && !isValidUrl(invalidSection.cta_url.trim())) {
      return "Section CTA URL is invalid";
    }
    if (hasImageUrl && !isValidUrl(invalidSection.image_url.trim())) {
      return "Section image URL is invalid";
    }
    return null;
  };

  const uploadSectionImage = async (index, file) => {
    const validationError = validateNewsletterImage(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setUploadingSectionIds((prev) => [...prev, index]);
    try {
      const uploadResult = await uploadNewsletterImage(file);

      setDraft((prev) => ({
        ...prev,
        sections: (prev.sections || []).map((section, sectionIndex) =>
          sectionIndex === index
            ? { ...section, image_url: uploadResult.public_url }
            : section,
        ),
      }));
      toast.success("Section image uploaded");
    } catch (error) {
      toast.error(error.message || "Unable to upload section image");
    } finally {
      setUploadingSectionIds((prev) => prev.filter((value) => value !== index));
    }
  };

  const uploadHeroImage = async (file) => {
    const validationError = validateNewsletterImage(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setHeroImageUploading(true);
    try {
      const uploadResult = await uploadNewsletterImage(file);
      updateTemplateField("hero_image_url", uploadResult.public_url);
      toast.success("Hero image uploaded");
    } catch (error) {
      toast.error(error.message || "Unable to upload hero image");
    } finally {
      setHeroImageUploading(false);
    }
  };

  const uploadSpotlightImage = async (file) => {
    const validationError = validateNewsletterImage(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSpotlightImageUploading(true);
    try {
      const uploadResult = await uploadNewsletterImage(file);
      updateTemplateField("feature_spotlight_image_url", uploadResult.public_url);
      toast.success("Spotlight image uploaded");
    } catch (error) {
      toast.error(error.message || "Unable to upload spotlight image");
    } finally {
      setSpotlightImageUploading(false);
    }
  };

  const handleSave = async () => {
    if (isSent) {
      toast.error("This newsletter has already been sent and can no longer be modified or resent.");
      return;
    }
    if (!draft.sender_email) {
      toast.error("Sender email is required");
      return;
    }
    if (!isValidEmail(draft.reply_to_email)) {
      toast.error("Reply-to email is invalid");
      return;
    }
    const sectionValidationError = validateSections();
    if (sectionValidationError) {
      toast.error(sectionValidationError);
      return;
    }
    setActionLoading("save");
    try {
      const payload = {
        subject: draft.subject,
        preheader: draft.preheader,
        hero_title: draft.hero_title,
        hero_copy: draft.hero_copy,
        cta_text: draft.cta_text,
        cta_url: draft.cta_url,
        sections: draft.sections,
        template_data: draft.template_data,
        sender_email: draft.sender_email,
        reply_to_email: draft.reply_to_email,
        campaign_list_key: draft.campaign_list_key,
      };
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/${id}`,
        {
          method: "PUT",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to save draft");
      }
      toast.success("Draft saved successfully");
      fetchDraft();
    } catch (error) {
      toast.error(error.message || "Unable to save draft");
    } finally {
      setActionLoading("");
    }
  };

  const regenerateImagePromptForSection = async (index) => {
    if (isSent) return;
    const section = (draft.sections || [])[index] || {};
    setRegeneratingPromptIds((prev) => [...prev, index]);
    try {
      const otherSections = (draft.sections || []).map((s) => ({
        title: s.title || "",
        body: s.body || "",
        image_prompt: s.image_prompt || "",
      }));
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/${id}/regenerate-image-prompt`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            section_index: index,
            section_title: section.title || "",
            section_body: section.body || "",
            current_image_prompt: section.image_prompt || "",
            subject: draft.subject || "",
            hero_title: draft.hero_title || "",
            hero_copy: draft.hero_copy || "",
            topic_title: draft.topic_title || "",
            other_sections: otherSections,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to regenerate image prompt");
      }
      const newPrompt = data?.data?.image_prompt || "";
      if (!newPrompt) {
        throw new Error("AI returned an empty prompt");
      }
      setDraft((prev) => ({
        ...prev,
        sections: (prev.sections || []).map((s, sIdx) =>
          sIdx === index ? { ...s, image_prompt: newPrompt } : s,
        ),
      }));
      toast.success("Image prompt regenerated");
    } catch (error) {
      toast.error(error.message || "Unable to regenerate image prompt");
    } finally {
      setRegeneratingPromptIds((prev) => prev.filter((v) => v !== index));
    }
  };

  const generateSectionWithAI = async (index, noteToAi = "") => {
    if (isSent) return;

    setGeneratingSectionIds((prev) => [...prev, index]);
    try {
      const previousSections = (draft.sections || [])
        .slice(0, index)
        .slice(-2)
        .map((section) => ({
          title: section.title || "",
          body: section.body || "",
          bullets: Array.isArray(section.bullets) ? section.bullets : [],
          cta_text: section.cta_text || "",
          cta_url: section.cta_url || "",
          theme: section.theme || "light",
        }));

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/${id}/generate-section`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            section_index: index,
            month: draft.month,
            topic_type: draft.topic_type,
            topic_title: draft.topic_title,
            subject: draft.subject,
            hero_title: draft.hero_title,
            hero_copy: draft.hero_copy,
            template_data: draft.template_data,
            previous_sections: previousSections,
            note_to_ai: noteToAi,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to generate section");
      }

      const nextSection = data?.data?.section || data?.data || {};

      setDraft((prev) => ({
        ...prev,
        sections: (prev.sections || []).map((section, sectionIndex) =>
          sectionIndex === index
            ? {
                ...section,
                title: nextSection.title || section.title || "",
                body: nextSection.body || section.body || "",
                bullets: Array.isArray(nextSection.bullets)
                  ? nextSection.bullets
                  : section.bullets || [],
                cta_text: nextSection.cta_text || section.cta_text || "",
                cta_url: nextSection.cta_url || section.cta_url || "",
                theme: nextSection.theme || section.theme || "light",
              }
            : section,
        ),
      }));
      toast.success("Section generated successfully");
    } catch (error) {
      toast.error(error.message || "Unable to generate section");
    } finally {
      setGeneratingSectionIds((prev) => prev.filter((value) => value !== index));
    }
  };

  const callStatusAction = async (action, extraPayload = {}) => {
    setActionLoading(action);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/${id}/${action}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body:
            action === "send"
              ? JSON.stringify({
                  ...buildCurrentDraftPayload(),
                  campaign_list_key:
                    extraPayload.campaign_list_key ||
                    draft.campaign_list_key ||
                    null,
                  ...extraPayload,
                })
              : undefined,
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || `Unable to ${action} draft`);
      }
      toast.success(data?.message || `Draft ${action}d successfully`);
      fetchDraft();
      if (action === "send") setSendOpen(false);
    } catch (error) {
      toast.error(error.message || `Unable to ${action} draft`);
    } finally {
      setActionLoading("");
    }
  };

  const buildCurrentDraftPayload = () => ({
    subject: draft.subject,
    preheader: draft.preheader,
    hero_title: draft.hero_title,
    hero_copy: draft.hero_copy,
    cta_text: draft.cta_text,
    cta_url: draft.cta_url,
    sections: draft.sections,
    template_data: draft.template_data,
    sender_name: draft.sender_name,
    sender_email: draft.sender_email,
    reply_to_email: draft.reply_to_email,
    campaign_list_key: draft.campaign_list_key,
  });

  const handleSendTest = async (email, resetInput) => {
    setActionLoading("send-test");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/${id}/send-test`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          // Include the current editor state so freshly-uploaded images and
          // any in-flight edits flow through, even if the user hasn't clicked
          // Save Changes yet.
          body: JSON.stringify({ email, ...buildCurrentDraftPayload() }),
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to send test email");
      }
      toast.success(data.message || "Test email sent");
      resetInput();
      setTestOpen(false);
    } catch (error) {
      toast.error(error.message || "Unable to send test email");
    } finally {
      setActionLoading("");
    }
  };

  const handleSyncAudience = async () => {
    setSyncLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/newsletters/sync-audience`,
        {
          method: "POST",
          credentials: "include",
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Unable to sync audience");
      }
      const failedCount = Number(data?.data?.failed || 0);
      if (failedCount > 0) {
        toast.warn(
          `Audience sync completed with issues. Total lists: ${data?.data?.total_lists || 0}, synced: ${data?.data?.synced || 0}, failed: ${failedCount}`,
        );
      } else {
        toast.success(
          `Audience sync completed. Total lists: ${data?.data?.total_lists || 0}, synced: ${data?.data?.synced || 0}, failed: 0`,
        );
      }
      fetchDraft();
    } catch (error) {
      toast.error(error.message || "Unable to sync audience");
    } finally {
      setSyncLoading(false);
    }
  };

  const metadata = useMemo(
    () => [
      ["Topic Type", draft.topic_type || "-"],
      ["Topic Title", draft.topic_title || "-"],
      ["Month", draft.month || "-"],
      ["Audience", effectiveAudienceCount],
      ["Last Audience Sync", formatNewsletterDate(draft.last_synced_audience_at)],
      ["Campaign List Key", draft.campaign_list_key || "-"],
      ["Campaign Provider", draft.campaign_provider || "Zoho Campaigns"],
      ["Campaign ID", draft.campaign_id || "-"],
    ],
    [draft, effectiveAudienceCount],
  );

  const campaignViewUrl = useMemo(() => {
    const explicitUrl = String(draft.campaign_url || "").trim();
    if (explicitUrl) return explicitUrl;
    const rawCampaignId = String(draft.campaign_id || "").trim();
    if (!rawCampaignId) {
      const orgId =
        String(process.env.REACT_APP_ZOHO_CAMPAIGNS_ORG_ID || "").trim() ||
        "60023557329";
      return `https://campaigns.zoho.in/campaigns/org${orgId}/home.do#emailcampaign/list/all`;
    }
    const orgId =
      String(process.env.REACT_APP_ZOHO_CAMPAIGNS_ORG_ID || "").trim() ||
      "60023557329";
    if (!/^\d+$/.test(rawCampaignId)) {
      return `https://campaigns.zoho.in/campaigns/org${orgId}/home.do#emailcampaign/list/all`;
    }
    return `https://campaigns.zoho.in/campaigns/org${orgId}/home.do#emailcampaign/${rawCampaignId}/cmpdetails`;
  }, [draft.campaign_id, draft.campaign_url]);

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={styles.pageShell}>
      <div className={styles.editorTopBar}>
        <div className={styles.editorTopLeft}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/dashboard/newsletters")}
            className={styles.backAction}
          >
            Back to newsletters
          </Button>
          <div>
            <div className={styles.eyebrow}>Zoho Campaigns Manual Send</div>
            <h1 className={styles.pageTitle}>{draft.topic_title || "Newsletter Draft"}</h1>
            {isSent ? (
              <p className={styles.pageSubtitle}>
                This newsletter has already been sent and can no longer be modified or resent.
              </p>
            ) : null}
          </div>
        </div>
        <div className={styles.editorTopRight}>
          <NewsletterStatusBadge status={draft.status} />
          <div className={styles.headerActionGroup}>
            <Button
              variant="outlined"
              startIcon={<VisibilityOutlinedIcon />}
              onClick={() => setPreviewOpen(true)}
              className={styles.secondaryAction}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveOutlinedIcon />}
              onClick={handleSave}
              disabled={Boolean(actionLoading) || isSent}
              className={styles.primaryAction}
            >
              {actionLoading === "save" ? "Saving..." : "Save Draft"}
            </Button>
            {isDraft ? (
              <Button
                variant="outlined"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={() => callStatusAction("approve")}
                disabled={Boolean(actionLoading)}
                className={styles.secondaryAction}
              >
                Approve
              </Button>
            ) : null}
            {isApproved ? (
              <Button
                variant="outlined"
                startIcon={<UndoOutlinedIcon />}
                onClick={() => callStatusAction("unapprove")}
                disabled={Boolean(actionLoading)}
                className={styles.secondaryAction}
              >
                Unapprove
              </Button>
            ) : null}
            <Button
              variant="outlined"
              onClick={() => setTestOpen(true)}
              disabled={Boolean(actionLoading) || isSent}
              className={styles.secondaryAction}
            >
              Send Test
            </Button>
            {isApproved ? (
              <Button
                variant="contained"
                startIcon={<SendOutlinedIcon />}
                onClick={() => setSendOpen(true)}
                disabled={Boolean(actionLoading)}
                className={styles.primaryActionDark}
              >
                Send Newsletter
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <div className={styles.editorGrid}>
        <div className={styles.editorColumn}>
          <div className={styles.editorCard}>
            <div className={styles.cardHeader}>
              <div>
                <h3>Metadata</h3>
                <p>Track campaign ownership, audience state, and publication status.</p>
              </div>
              <Button
                variant="outlined"
                onClick={handleSyncAudience}
                disabled={syncLoading}
                className={styles.secondaryAction}
              >
                {syncLoading ? "Syncing..." : "Sync Audience"}
              </Button>
            </div>
            {campaignViewUrl && hasZohoCampaign ? (
              <div style={{ marginBottom: 16 }}>
                <Button
                  variant="outlined"
                  className={styles.secondaryAction}
                  href={campaignViewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenInNewOutlinedIcon />}
                >
                  Open In Zoho Campaigns
                </Button>
              </div>
            ) : null}
            <div className={styles.metaGrid}>
              {metadata.map(([label, value]) => (
                <div key={label} className={styles.metaItem}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.editorCard}>
            <div className={styles.cardHeader}>
              <div>
                <h3>Sender Settings</h3>
                <p>Review the verified sender and reply-to before approval and send.</p>
              </div>
            </div>

            <div className={styles.formStack}>
              {senderOptionsError && <Alert severity="warning">{senderOptionsError}</Alert>}
              <TextField
                label="Sender Name"
                value={draft.sender_name || senderOptions.sender_name || "Easexpense"}
                fullWidth
                InputProps={{ readOnly: true }}
                disabled={isSent}
              />
              <div className={styles.inlineFields}>
                <FormControl fullWidth disabled={senderOptionsLoading || isSent}>
                  <InputLabel id="newsletter-sender-email-label">Sender Email</InputLabel>
                  <Select
                    labelId="newsletter-sender-email-label"
                    label="Sender Email"
                    value={draft.sender_email || ""}
                    onChange={(e) => updateDraftField("sender_email", e.target.value)}
                  >
                    {(senderOptions.sender_emails || []).map((item) => (
                      <MenuItem key={item.email} value={item.email}>
                        {item.label || item.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth disabled={campaignListsLoading || isSent}>
                  <InputLabel id="newsletter-campaign-list-label">
                    Campaign List
                  </InputLabel>
                  <Select
                    labelId="newsletter-campaign-list-label"
                    label="Campaign List"
                    value={draft.campaign_list_key || ""}
                    onChange={(e) =>
                      updateDraftField("campaign_list_key", e.target.value)
                    }
                  >
                    {(campaignLists || []).map((item) => (
                      <MenuItem
                        key={item.list_key}
                        value={item.list_key}
                      >
                        {item.list_name} ({getCampaignListAudienceCount(item) ?? "-"})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              {campaignListsError ? (
                <Alert severity="warning">{campaignListsError}</Alert>
              ) : null}
              <div className={styles.inlineFields}>
                <TextField
                  label="Reply-To Email"
                  value={draft.reply_to_email || ""}
                  onChange={(e) =>
                    updateDraftField("reply_to_email", e.target.value.trim())
                  }
                  error={Boolean(draft.reply_to_email) && !isValidEmail(draft.reply_to_email)}
                  helperText={
                    Boolean(draft.reply_to_email) && !isValidEmail(draft.reply_to_email)
                      ? "Enter a valid email address"
                      : "Optional. Replies from recipients will go here."
                  }
                  fullWidth
                  disabled={isSent}
                />
              </div>
            </div>
          </div>

          <div className={styles.editorCard}>
            <div className={styles.cardHeader}>
              <h3>Content Editor</h3>
              <p>Edit the campaign copy before approval and send.</p>
            </div>

            <div className={styles.formStack}>
              <TextField
                label="Subject"
                value={draft.subject || ""}
                onChange={(e) => updateDraftField("subject", e.target.value)}
                fullWidth
                disabled={isSent}
              />
              <TextField
                label="Preheader"
                value={draft.preheader || ""}
                onChange={(e) => updateDraftField("preheader", e.target.value)}
                fullWidth
                disabled={isSent}
              />
              <TextField
                label="Hero Title"
                value={draft.hero_title || ""}
                onChange={(e) => updateDraftField("hero_title", e.target.value)}
                fullWidth
                disabled={isSent}
              />
              <TextField
                label="Hero Copy"
                value={draft.hero_copy || ""}
                onChange={(e) => updateDraftField("hero_copy", e.target.value)}
                fullWidth
                multiline
                minRows={4}
                disabled={isSent}
              />
              <div className={styles.inlineFields}>
                <TextField
                  label="CTA Text"
                  value={draft.cta_text || ""}
                  onChange={(e) => updateDraftField("cta_text", e.target.value)}
                  fullWidth
                  disabled={isSent}
                />
                <TextField
                  label="CTA URL"
                  value={draft.cta_url || ""}
                  onChange={(e) => updateDraftField("cta_url", e.target.value)}
                  fullWidth
                  disabled={isSent}
                />
              </div>
              {/* Hero image — standalone block, renders above the spotlight
                  even when the spotlight is turned off. */}
              <TextField
                label="Hero Image URL"
                value={draft?.template_data?.hero_image_url || ""}
                onChange={(e) => updateTemplateField("hero_image_url", e.target.value)}
                helperText="Standalone hero visual that sits above the feature spotlight. Recommended size: 1200 x 720 px."
                fullWidth
                disabled={isSent}
              />
              <div className={styles.sectionImageToolbar}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={
                    heroImageUploading ? <CircularProgress size={16} /> : null
                  }
                  disabled={heroImageUploading || isSent}
                  className={styles.secondaryAction}
                >
                  {heroImageUploading ? "Uploading..." : "Upload Hero Image"}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        uploadHeroImage(file);
                      }
                      e.target.value = "";
                    }}
                  />
                </Button>
                <span className={styles.imageHint}>
                  Suggested pixel size: 1200 x 720 px
                </span>
                {draft?.template_data?.hero_image_url ? (
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() => updateTemplateField("hero_image_url", "")}
                    disabled={isSent}
                  >
                    Remove Hero Image
                  </Button>
                ) : null}
              </div>
              {draft?.template_data?.hero_image_url ? (
                <div className={styles.sectionImagePreview}>
                  <img
                    src={draft.template_data.hero_image_url}
                    alt={draft?.template_data?.hero_feature_name || "Hero"}
                  />
                </div>
              ) : null}

              {/* Feature Spotlight — toggleable. When off, the dark spotlight
                  card and its dedicated image are both omitted from the
                  final email. Hero image above is unaffected. */}
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      draft?.template_data?.feature_spotlight_enabled !== false
                    }
                    onChange={(e) =>
                      updateTemplateField(
                        "feature_spotlight_enabled",
                        e.target.checked,
                      )
                    }
                    disabled={isSent}
                  />
                }
                label="Show Feature Spotlight section"
              />
              {draft?.template_data?.feature_spotlight_enabled !== false && (
                <>
                  <div className={styles.inlineFields}>
                    <TextField
                      label="Spotlight Name"
                      value={
                        draft?.template_data?.hero_feature_name ||
                        draft?.topic_title ||
                        ""
                      }
                      onChange={(e) =>
                        updateTemplateField("hero_feature_name", e.target.value)
                      }
                      fullWidth
                      disabled={isSent}
                    />
                    <TextField
                      label="Spotlight Description"
                      value={
                        draft?.template_data?.hero_feature_description ||
                        draft?.hero_copy ||
                        ""
                      }
                      onChange={(e) =>
                        updateTemplateField("hero_feature_description", e.target.value)
                      }
                      fullWidth
                      disabled={isSent}
                    />
                  </div>
                  <TextField
                    label="Spotlight Image URL (optional)"
                    value={
                      draft?.template_data?.feature_spotlight_image_url || ""
                    }
                    onChange={(e) =>
                      updateTemplateField(
                        "feature_spotlight_image_url",
                        e.target.value,
                      )
                    }
                    helperText="Dedicated visual shown inside the spotlight card. Leave empty for a text-only spotlight."
                    fullWidth
                    disabled={isSent}
                  />
                  <div className={styles.sectionImageToolbar}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={
                        spotlightImageUploading ? <CircularProgress size={16} /> : null
                      }
                      disabled={spotlightImageUploading || isSent}
                      className={styles.secondaryAction}
                    >
                      {spotlightImageUploading ? "Uploading..." : "Upload Spotlight Image"}
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            uploadSpotlightImage(file);
                          }
                          e.target.value = "";
                        }}
                      />
                    </Button>
                    <span className={styles.imageHint}>
                      Suggested pixel size: 800 x 600 px
                    </span>
                    {draft?.template_data?.feature_spotlight_image_url ? (
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() =>
                          updateTemplateField("feature_spotlight_image_url", "")
                        }
                        disabled={isSent}
                      >
                        Remove Spotlight Image
                      </Button>
                    ) : null}
                  </div>
                  {draft?.template_data?.feature_spotlight_image_url ? (
                    <div className={styles.sectionImagePreview}>
                      <img
                        src={draft.template_data.feature_spotlight_image_url}
                        alt={
                          draft?.template_data?.hero_feature_name || "Spotlight"
                        }
                      />
                    </div>
                  ) : null}
                </>
              )}

              {/* Highlighted Capability — toggleable. When off, the entire
                  "Pairs Well With" card is omitted from the email. */}
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      draft?.template_data?.highlight_capability_enabled !== false
                    }
                    onChange={(e) =>
                      updateTemplateField(
                        "highlight_capability_enabled",
                        e.target.checked,
                      )
                    }
                    disabled={isSent}
                  />
                }
                label="Show Highlighted Capability section"
              />
              {draft?.template_data?.highlight_capability_enabled !== false && (
                <div className={styles.inlineFields}>
                  <TextField
                    label="Highlighted Capability Name"
                    value={
                      draft?.template_data?.feature_name ||
                      draft?.template_data?.product_name ||
                      ""
                    }
                    onChange={(e) => updateTemplateField("feature_name", e.target.value)}
                    fullWidth
                    disabled={isSent}
                  />
                  <TextField
                    label="Highlighted Capability Description"
                    value={
                      draft?.template_data?.feature_description ||
                      draft?.template_data?.product_description ||
                      ""
                    }
                    onChange={(e) =>
                      updateTemplateField("feature_description", e.target.value)
                    }
                    fullWidth
                    disabled={isSent}
                  />
                </div>
              )}

              <NewsletterSectionEditor
                sections={draft.sections || []}
                setSections={(sectionsUpdater) =>
                  setDraft((prev) => ({
                    ...prev,
                    sections:
                      typeof sectionsUpdater === "function"
                        ? sectionsUpdater(prev.sections || [])
                        : sectionsUpdater,
                  }))
                }
                onImageUpload={uploadSectionImage}
                onGenerateSection={(index) =>
                  setSectionAiDialog({ open: true, index, note: "" })
                }
                onRegenerateImagePrompt={regenerateImagePromptForSection}
                uploadingSectionIds={uploadingSectionIds}
                generatingSectionIds={generatingSectionIds}
                regeneratingPromptIds={regeneratingPromptIds}
                disabled={isSent}
              />
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={sectionAiDialog.open}
        onClose={() =>
          setSectionAiDialog({ open: false, index: null, note: "" })
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Generate Section With AI</DialogTitle>
        <DialogContent className={styles.generateDialogBody}>
          <TextField
            label="Additional Note to AI (Optional)"
            multiline
            minRows={4}
            value={sectionAiDialog.note}
            onChange={(e) =>
              setSectionAiDialog((prev) => ({ ...prev, note: e.target.value }))
            }
            helperText="Add any extra context for this section generation."
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setSectionAiDialog({ open: false, index: null, note: "" })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              sectionAiDialog.index === null ||
              generatingSectionIds.includes(sectionAiDialog.index)
            }
            onClick={async () => {
              const idx = sectionAiDialog.index;
              const note = sectionAiDialog.note;
              setSectionAiDialog({ open: false, index: null, note: "" });
              if (idx !== null) {
                await generateSectionWithAI(idx, note);
              }
            }}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fullWidth
        maxWidth="lg"
        className={styles.previewDialog}
      >
        <DialogTitle className={styles.previewDialogTitle}>
          <div>
            <h3>Newsletter Preview</h3>
            <p>Inspect the responsive email in a dedicated canvas.</p>
          </div>
          <IconButton onClick={() => setPreviewOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={styles.previewDialogBody}>
          <NewsletterPreview
            htmlPreview={previewHtml}
            draft={draft}
            viewport={previewViewport}
            setViewport={setPreviewViewport}
            previewError={previewError}
          />
        </DialogContent>
      </Dialog>

      <SendTestModal
        open={testOpen}
        onClose={() => setTestOpen(false)}
        onSubmit={handleSendTest}
        loading={actionLoading === "send-test"}
      />

      <SendConfirmModal
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        onConfirm={(payload) => callStatusAction("send", payload)}
        loading={actionLoading === "send"}
        audienceCount={effectiveAudienceCount}
      />
    </div>
  );
}

export default NewsletterEditorPage;
